import React, { useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";

import * as Action from "../../../store/redux/admin_reducer";
import "./managementlogin.css";

import {
  ArrowForwardRounded,
  CheckCircleOutlineRounded,
  LockOutlined,
  MailOutlineRounded,
  ShieldOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import Logo from "../public-routes-images/logos/Manga_Cons _Logo3.png";

let adminData;

const initialLoginState = {
  user_name: "",
  admin_password: "",
};

export default function ManagementLogin() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const redirect = useNavigate();

  useSelector((state) => state.adminFunction);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [adminLoginData, setAdminLoginData] = useReducer(
    (request, response) => ({
      ...request,
      ...response,
    }),
    initialLoginState
  );

  const isFormValid =
    adminLoginData.user_name.trim() !== "" &&
    adminLoginData.admin_password.trim() !== "";

  const togglePasswordVisibility = () => {
    setShowPassword((previous) => !previous);
  };

  const DispatchAdminData = () => async (dispatch) => {
    try {
      dispatch(Action.startAdminAction({ adminData }));
    } catch (error) {
      console.error("Admin dispatch error:", error);
    }
  };

  const executeLogin = async () => {
    if (loading) return;

    if (!adminLoginData.user_name.trim()) {
      enqueueSnackbar("Please enter your email address.", {
        variant: "warning",
      });
      return;
    }

    if (!adminLoginData.admin_password.trim()) {
      enqueueSnackbar("Please enter your password.", {
        variant: "warning",
      });
      return;
    }

    const data = {
      user_name: adminLoginData.user_name.trim(),
      admin_password: adminLoginData.admin_password,
    };

    try {
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/admin_login`,
        data
      );

      adminData = response?.data?.info;

      if (response?.data?.status === 201) {
        await dispatch(DispatchAdminData());

        if (rememberMe) {
          localStorage.setItem(
            "manga_cons_admin_username",
            adminLoginData.user_name.trim()
          );
        } else {
          localStorage.removeItem("manga_cons_admin_username");
        }

        enqueueSnackbar("Welcome back. Authentication successful.", {
          variant: "success",
          autoHideDuration: 3000,
        });

        redirect("/admin_profile");
        return;
      }

      if (response?.data?.status === 401) {
        enqueueSnackbar(
          response?.data?.message || "Invalid administrator credentials.",
          {
            variant: "error",
            autoHideDuration: 3500,
          }
        );

        return;
      }

      enqueueSnackbar(
        response?.data?.message || "Unable to authenticate your account.",
        {
          variant: "error",
        }
      );
    } catch (error) {
      console.error("Admin login error:", error);

      enqueueSnackbar(
        error?.response?.data?.message ||
          "Unable to connect to the authentication service.",
        {
          variant: "error",
          autoHideDuration: 4000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      executeLogin();
    }
  };

  return (
    <section className="management-login">
      <div className="management-login__ambient management-login__ambient--one" />
      <div className="management-login__ambient management-login__ambient--two" />

      <div className="management-login__shell">
        {/* =========================================================
            LEFT BRAND PANEL
        ========================================================= */}
        <aside className="management-login__brand">
          <div className="management-login__brand-overlay" />

          <div className="management-login__brand-content">
            <div className="management-login__brand-top">
              <div className="management-login__logo">
                <img src={Logo} alt="Manga Cons logo" />
              </div>

              <div className="management-login__secure-badge">
                <ShieldOutlined />
                <span>Secure workspace</span>
              </div>
            </div>

            <div className="management-login__brand-copy">
              <span className="management-login__eyebrow">
                MANGA CONS MANAGEMENT
              </span>

              <h1>
                MCONS administrative
                <span> command center.</span>
              </h1>

              <p>
                Manage admissions, applicants, academic operations and
                institutional workflows from one secure administrative
                workspace.
              </p>
            </div>

            <div className="management-login__features">
              <div className="management-login__feature">
                <div className="management-login__feature-icon">
                  <CheckCircleOutlineRounded />
                </div>

                <div>
                  <strong>Centralized administration</strong>
                  <span>
                    Everything your administrative team needs in one place.
                  </span>
                </div>
              </div>

              <div className="management-login__feature">
                <div className="management-login__feature-icon">
                  <CheckCircleOutlineRounded />
                </div>

                <div>
                  <strong>Protected access</strong>
                  <span>
                    Administrative information stays behind authenticated
                    access.
                  </span>
                </div>
              </div>

              <div className="management-login__feature">
                <div className="management-login__feature-icon">
                  <CheckCircleOutlineRounded />
                </div>

                <div>
                  <strong>Operational visibility</strong>
                  <span>
                    Access the tools and information needed to keep operations
                    moving.
                  </span>
                </div>
              </div>
            </div>

            <div className="management-login__brand-footer">
              <span>Administrative portal</span>
              <span className="management-login__footer-dot" />
              <span>Manga College of Nursing Sciences, Zuru</span>
            </div>
          </div>
        </aside>

        {/* =========================================================
            LOGIN PANEL
        ========================================================= */}
        <main className="management-login__main">
          <div className="management-login__mobile-logo rwdHide">
            <img src={Logo} alt="Manga Cons logo" />
          </div>

          <div className="management-login__form-wrapper">
            <div className="management-login__heading">
              <div className="management-login__heading-icon">
                <ShieldOutlined />
              </div>

              <div>
                <span className="management-login__section-label">
                  ADMINISTRATOR ACCESS
                </span>

                <h2>Welcome back</h2>

                <p>
                  Sign in to continue to your administrative workspace.
                </p>
              </div>
            </div>

            <form
              className="management-login__form"
              onSubmit={(event) => {
                event.preventDefault();
                executeLogin();
              }}
            >
              {/* EMAIL */}
              <div className="management-login__field">
                <label htmlFor="management-email">
                  Email address
                </label>

                <div
                  className={`management-login__input ${
                    adminLoginData.user_name
                      ? "management-login__input--filled"
                      : ""
                  }`}
                >
                  <MailOutlineRounded className="management-login__input-icon" />

                  <input
                    id="management-email"
                    name="user_name"
                    value={adminLoginData.user_name}
                    onChange={(event) =>
                      setAdminLoginData({
                        user_name: event.target.value,
                      })
                    }
                    onKeyDown={handleKeyDown}
                    type="email"
                    autoComplete="username"
                    placeholder="Enter your email address"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div className="management-login__field">
                <div className="management-login__label-row">
                  <label htmlFor="management-password">
                    Password
                  </label>

                  <button
                    type="button"
                    className="management-login__forgot"
                    onClick={() =>
                      enqueueSnackbar(
                        "Please contact your system administrator to reset your password.",
                        {
                          variant: "info",
                        }
                      )
                    }
                  >
                    Forgot password?
                  </button>
                </div>

                <div
                  className={`management-login__input ${
                    adminLoginData.admin_password
                      ? "management-login__input--filled"
                      : ""
                  }`}
                >
                  <LockOutlined className="management-login__input-icon" />

                  <input
                    id="management-password"
                    name="admin_password"
                    value={adminLoginData.admin_password}
                    onChange={(event) =>
                      setAdminLoginData({
                        admin_password: event.target.value,
                      })
                    }
                    onKeyDown={handleKeyDown}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="management-login__password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    disabled={loading}
                  >
                    {showPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </button>
                </div>
              </div>

              {/* REMEMBER */}
              <div className="management-login__options">
                <label className="management-login__remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) =>
                      setRememberMe(event.target.checked)
                    }
                    disabled={loading}
                  />

                  <span className="management-login__checkbox">
                    <span />
                  </span>

                  <span>Keep me signed in</span>
                </label>

                <span className="management-login__security-note">
                  <LockOutlined />
                  Secure login
                </span>
              </div>

              {/* LOGIN BUTTON */}
              <button
                type="submit"
                className={`management-login__submit ${
                  loading
                    ? "management-login__submit--loading"
                    : ""
                }`}
                disabled={loading || !isFormValid}
              >
                {loading ? (
                  <>
                    <span className="management-login__spinner" />
                    <span>Authenticating...</span>
                  </>
                ) : (
                  <>
                    <span>Sign in to workspace</span>
                    <ArrowForwardRounded />
                  </>
                )}
              </button>
            </form>

            <div className="management-login__divider">
              <span />
              <small>AUTHORIZED PERSONNEL ONLY</small>
              <span />
            </div>

            <div className="management-login__back">
              <Link to="/">
                <span>←</span>
                Back to Manga Cons home
              </Link>
            </div>

            <div className="management-login__legal">
              <span>
                © {new Date().getFullYear()} Manga College of Nursing
                Sciences, Zuru
              </span>

              <span className="management-login__legal-dot" />

              <span>Administrative portal</span>
            </div>
          </div>
        </main>
      </div>
    </section>
  );
}